import { ReactNode } from 'react';
import { RELATION_OWNED_BY, RELATION_PART_OF } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import {
  EntityRefLinks,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { entityTier } from '../ServiceTier';
import { Scorecard } from './useScorecard';

const useStyles = makeStyles(theme => ({
  strip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    marginBottom: theme.spacing(3),
  },
  tile: {
    padding: theme.spacing(1.5, 2),
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderRight: 'none' },
  },
  label: {
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
}));

export const Tile = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.tile}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography variant="body1" component="div" style={{ fontWeight: 600 }}>
        {children}
      </Typography>
    </Box>
  );
};

export const Strip = ({ children }: { children: ReactNode }) => {
  const classes = useStyles();
  return <Box className={classes.strip}>{children}</Box>;
};

export const scoreColor = (failing: number) => {
  if (failing === 0) return '#2e7d32';
  return failing <= 5 ? '#ed6c02' : '#d32f2f';
};

const ScoreTile = ({ scorecard }: { scorecard: Scorecard }) => (
  <Tile label="Scorecard">
    {scorecard.loading || !scorecard.determined ? (
      '-'
    ) : (
      <span style={{ color: scoreColor(scorecard.failing) }}>
        {scorecard.passing} of {scorecard.determined} passing
      </span>
    )}
  </Tile>
);

/** The facts an engineer wants first: who owns it, how it is doing, where the code is. */
export const StatsStrip = ({ scorecard }: { scorecard: Scorecard }) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  const systems = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const tier = entityTier(entity);
  const slug = entity.metadata.annotations?.['github.com/project-slug'];

  return (
    <Box className={classes.strip}>
      <Tile label="Owner">
        {owners.length ? (
          <EntityRefLinks entityRefs={owners} defaultKind="group" />
        ) : (
          'Unowned'
        )}
      </Tile>
      <Tile label="System">
        {systems.length ? <EntityRefLinks entityRefs={systems} /> : '-'}
      </Tile>
      <Tile label="Tier">{tier ? `Tier ${tier}` : 'Not set'}</Tile>
      <Tile label="Lifecycle">{String(entity.spec?.lifecycle ?? '-')}</Tile>
      <ScoreTile scorecard={scorecard} />
      <Tile label="Repository">
        {slug ? (
          <Link to={`https://github.com/${slug}`}>{slug.split('/')[1]}</Link>
        ) : (
          '-'
        )}
      </Tile>
    </Box>
  );
};
